import { AfterContentInit, ContentChild, Directive, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ToolbarComponent } from '../../embedded-view/toolbar/toolbar.component';
import { EntityType } from '../../rl-common/shared/shared/entity-type.enum';
import { Visibility } from '../../rl-common/shared/shared/types/visibility.enum';
import { AnnotatorCommentsPanelComponent } from '../annotator-comments-panel/annotator-comments-panel.component';
import { AnnotationsStorageService } from '../core/annotations-storage.service';
import { AnnotatorJSService } from '../core/annotator-js.service';
import { SearchParams } from '../core/types';
import { Annotation } from './annotation';
import { AnnotationConfig } from './annotation-config';
import { AnnotatorCommentBtnDirective } from './annotator-comment-btn.directive';
import { AnnotatorConfig } from './annotator-config';
import { AnnotatorFileDirective } from './annotator-file.directive';
import { Type } from './Tools/annotator-tool';

@Directive({
  selector: '[rlAnnotatorContainer]'
})
export class AnnotatorContainerDirective implements OnInit, AfterContentInit, OnDestroy {
  @Input() config: AnnotatorConfig;

  @ContentChild(AnnotatorCommentsPanelComponent) commentWrapper: AnnotatorCommentsPanelComponent;
  @ContentChild(AnnotatorCommentBtnDirective, { read: ElementRef }) commentBtn: ElementRef;
  @ContentChild(AnnotatorFileDirective) file: AnnotatorFileDirective;
  @ContentChild(ToolbarComponent) toolbar: ToolbarComponent;

  tools: any = {};
  undrawAnnsSub: Subscription;
  drawAnnsSub: Subscription;
  toolsDisabledSub: Subscription;
  visibility: Visibility;

  constructor(private annStorage: AnnotationsStorageService, private plugin: AnnotatorJSService, private element: ElementRef) {}

  ngOnInit(): void {
    if (!this.config.enable) return;
    this.tools = this.plugin.init(this.config.toolsDisabled.collection);
    this.undrawAnnsSub = this.config.unDrawAnns.subscribe((config: AnnotationConfig[]) => this.annStorage.removeAnnotations(config));
    this.drawAnnsSub = this.config.drawAnns.subscribe((config: AnnotationConfig[]) => config.forEach((ac) => this.loadAnns(ac)));
    this.toolsDisabledSub = this.config.toolsDisabledChanged.subscribe((arr: any[]) => this.disableTools(arr));
  }

  ngAfterContentInit(): void {
    //this.commentBtn.nativeElement.click();
    this.commentWrapper.isCloseable = this.config.canCloseCommentsPanel;
    this.commentWrapper.messages = this.config.commentsPanelMessages;
    this.commentWrapper.actionBtns = this.config.commentsPanelActions;
    this.commentWrapper.displayPrivatePublicTabs = this.config.displayPrivatePublicTabs;
    this.disableTools(this.config.toolsDisabled.collection);

    this.annStorage.getVisibility().subscribe((visibility) => {
      this.visibility = visibility;
      if (visibility === Visibility.PUBLIC) {
        this.config.toolsDisabled.push(Type.highlight);
        this.tools.HL.disabled = true;
        this.plugin.selectTool(Type.none, undefined);
        this.toolbar.onSelectTool.emit(Type.none);
      } else {
        this.config.toolsDisabled.remove(Type.highlight);
        this.tools.HL.disabled = false;
      }
    });
  }

  disableTools(arr: any[]) {
    this.toolbar.disableDesignTools(arr);
    if (this.config.toolsDisabled.collection.indexOf('CMT') > -1) {
      this.commentWrapper.disableEditComments();
    }
    this.plugin.disable(arr);
  }

  @HostListener('lgntoann.selection.new')
  newSelection = () => (this.commentWrapper.showTextEditor = false);

  @HostListener('lgntoann.annotation.trash', ['$event'])
  trash = (e) => (e.detail.commentAttached ? this.commentWrapper.deleteFromPdf(e) : this.annStorage.delete(e.detail.id));

  @HostListener('lgntoann.annotation.create', ['$event'])
  createAnnotation(e) {
    let tool = this.tools[e.detail.tool];
    //tool= higlight comment shape
    if (!tool || tool.disabled) return;
    //create annotation obj
    let newAnn: Annotation = e.detail.ann;
    newAnn.type = tool.code;
    newAnn.entityId = this.config.mainAnnotationConfig.entityId;
    newAnn.entityType = this.config.mainAnnotationConfig.entityType;
    //get the anno attributes to the ann fron the session
    this.addToolAttributes(newAnn);
    this.config.onNewAnnotation(newAnn);
    newAnn.visibility = this.getNewCommentVisibility(newAnn);

    if (tool.attachComment) {
      //open the comment panel
      if (this.commentBtn && !this.commentBtn.nativeElement.classList.contains('btn-icon-only')) this.commentBtn.nativeElement.click();
      this.commentWrapper.showTextEditor = true;
      //open for edit and save
      this.annStorage.editNewAnnotation(newAnn);
    } else {
      //do save dont open
      this.annStorage.saveNewAnnotation(newAnn);
    }
  }

  getNewCommentVisibility(newAnn: Annotation) {
    if (newAnn.entityType === EntityType.CITATION) {
      return newAnn.type === Type.highlight ? Visibility.PRIVATE : this.visibility;
    } else if (newAnn.entityType === EntityType.ASSIGNMENT || newAnn.entityType === EntityType.SUBMISSION) {
      return Visibility.PUBLIC;
    }
  }

  @HostListener('lgntoann.annotation.addComment', ['$event'])
  addCommentToAnnotation(e) {
    if (this.commentBtn && !this.commentBtn.nativeElement.classList.contains('btn-icon-only')) this.commentBtn.nativeElement.click();
    this.commentWrapper.showTextEditor = true;
    this.annStorage.addCommentToAnnotation(e.detail.ann);
  }

  addToolAttributes(ann: Annotation) {
    let attr = sessionStorage.getItem('lgnto.anntoolAttributes');
    if (!attr) return;
    attr = JSON.parse(attr);
    Object.assign(ann, attr);
    ann.attributes = attr;
  }

  loadPage = (pageNumber: number, rotation: number) => {
    sessionStorage.setItem('lgnto.fileRotation', '' + rotation);
    this.plugin.newPage(pageNumber);
    this.annStorage.drawAnnotations(pageNumber, rotation, this.visibility);
  };

  start() {
    this.plugin.start(this.file.getConfig(this.element));
    this.annStorage.init({
      entityId: this.config.mainAnnotationConfig.entityId,
      fileId: this.config.fileId,
      entityType: this.config.mainAnnotationConfig.entityType
    });
    if (this.config.mainAnnotationConfig.entityType === EntityType.ASSIGNMENT || this.config.mainAnnotationConfig.entityType === EntityType.SUBMISSION) {
      this.annStorage.setVisibility(Visibility.PUBLIC);
      this.config.toolsDisabled.push(Type.highlight);
    }

    this.config.additionalAnnotationConfig.forEach((c) => this.loadAnns(c));
  }

  loadAnns(config: AnnotationConfig) {
    this.annStorage.load(<SearchParams>(<unknown>Object.assign(config, { fileId: this.config.fileId })));
  }

  ngOnDestroy() {
    this.undrawAnnsSub.unsubscribe();
    this.drawAnnsSub.unsubscribe();
    this.toolsDisabledSub.unsubscribe();
  }
}
